import { Briefcase, Building2, Plus, Store, Trash2, User, UserPlus, Users } from 'lucide-react';
import { Stakeholder } from '../../../helpers/utils/types';
import { BusinessFormState, StakeholderFormState } from '../profileTypes';
import SettingsGroup from './SettingsGroup';

type BusinessTypeOption = { kycBusinessTypeId: string; value: string };

const fieldInputClass =
  'w-full rounded-[10px] border border-[#DEE2EF] bg-white px-3 py-2.5 text-[14px] font-normal text-main outline-none transition-colors placeholder:text-[#9AA0AE] focus:border-[#0055FF]';

type BusinessDetailsCardProps = {
  businessForm: BusinessFormState;
  stakeholders: Stakeholder[];
  stakeholderForm: StakeholderFormState;
  kycBusinessTypes: BusinessTypeOption[];
  editingStakeholderId: string | null;
  savingBusiness: boolean;
  savingStakeholder: boolean;
  onBusinessFormChange: (next: BusinessFormState) => void;
  onStakeholderFormChange: (next: StakeholderFormState) => void;
  onSaveBusiness: () => void;
  onSaveStakeholder: () => void;
  onEditStakeholder: (stakeholder: Stakeholder) => void;
  onDeleteStakeholder: (id: string) => void;
};

const iconWrapClass =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EEF5FF] text-[#0055FF]';

/** Dashboard-style pill: `btn-blue` only (compact). Avoid `btn-blue-lg` for profile row actions. */
const dashboardPrimaryCtaClass =
  'btn-blue inline-flex w-auto max-w-full items-center justify-center gap-1.5 disabled:pointer-events-none disabled:opacity-50 disabled:hover:transform-none';

const BusinessDetailsCard = ({
  businessForm,
  stakeholders,
  stakeholderForm,
  kycBusinessTypes,
  editingStakeholderId,
  savingBusiness,
  savingStakeholder,
  onBusinessFormChange,
  onStakeholderFormChange,
  onSaveBusiness,
  onSaveStakeholder,
  onEditStakeholder,
  onDeleteStakeholder,
}: BusinessDetailsCardProps) => (
  <SettingsGroup title='Business details'>
    <div className='flex items-start gap-4 px-5 py-4'>
      <div className={`${iconWrapClass} mt-0.5`}>
        <Building2 size={14} />
      </div>
      <div className='min-w-0 flex-1 space-y-2'>
        <label htmlFor='profile-business-legal-name' className='block text-[14px] font-semibold text-main'>
          Company legal name
        </label>
        <input
          id='profile-business-legal-name'
          value={businessForm.name}
          onChange={(e) => onBusinessFormChange({ ...businessForm, name: e.target.value })}
          className={fieldInputClass}
          placeholder='Enter company legal name'
        />
      </div>
    </div>

    <div className='flex items-start gap-4 px-5 py-4'>
      <div className={`${iconWrapClass} mt-0.5`}>
        <Store size={14} />
      </div>
      <div className='min-w-0 flex-1 space-y-2'>
        <label htmlFor='profile-business-display-name' className='block text-[14px] font-semibold text-main'>
          Business name <span className='font-normal text-[#6B7285]'>(optional)</span>
        </label>
        <input
          id='profile-business-display-name'
          value={businessForm.displayName}
          onChange={(e) => onBusinessFormChange({ ...businessForm, displayName: e.target.value })}
          className={fieldInputClass}
          placeholder='Enter business display name'
        />
      </div>
    </div>

    <div className='flex items-start gap-4 px-5 py-4'>
      <div className={`${iconWrapClass} mt-0.5`}>
        <Briefcase size={14} />
      </div>
      <div className='min-w-0 flex-1 space-y-2'>
        <label htmlFor='profile-business-type' className='block text-[14px] font-semibold text-main'>
          Business type
        </label>
        <select
          id='profile-business-type'
          value={businessForm.kycBusinessTypeId}
          onChange={(e) => onBusinessFormChange({ ...businessForm, kycBusinessTypeId: e.target.value })}
          className={`${fieldInputClass} appearance-none bg-[length:16px] bg-[position:right_12px_center] bg-no-repeat pr-10`}
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239AA0AE' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
          }}
        >
          <option value=''>Select business type</option>
          {kycBusinessTypes.map((type) => (
            <option key={type.kycBusinessTypeId} value={type.kycBusinessTypeId}>
              {type.value}
            </option>
          ))}
        </select>
      </div>
    </div>

    <div className='flex justify-end px-5 py-4'>
      <button
        type='button'
        onClick={onSaveBusiness}
        disabled={savingBusiness}
        className={dashboardPrimaryCtaClass}
      >
        <Building2 size={12} strokeWidth={2} />
        {savingBusiness ? 'Saving...' : 'Save business details'}
      </button>
    </div>

    <div className='flex items-center gap-4 px-5 py-4'>
      <div className={iconWrapClass}>
        <Users size={14} />
      </div>
      <div className='min-w-0 flex-1'>
        <div className='text-[14px] font-semibold text-main'>Stakeholders</div>
        <div className='mt-0.5 text-[12px] text-[#6B7285]'>
          {stakeholders.length === 0
            ? 'No stakeholders added yet.'
            : `${stakeholders.length} ${stakeholders.length === 1 ? 'person' : 'people'} listed`}
        </div>
      </div>
    </div>

    {stakeholders.map((item) => (
      <div
        key={item.tenantStakeholderId}
        className='flex items-center gap-4 px-5 py-4'
      >
        <div className={iconWrapClass}>
          <User size={14} />
        </div>
        <div className='min-w-0 flex-1'>
          <div className='text-[14px] font-semibold text-main'>
            {item.firstName} {item.lastName}
          </div>
          <div className='mt-0.5 truncate text-[12px] text-[#6B7285]'>{item.email}</div>
        </div>
        <div className='flex shrink-0 items-center gap-3'>
          <button
            type='button'
            className='text-[13px] font-semibold text-[#0055FF] hover:underline'
            onClick={() => onEditStakeholder(item)}
          >
            Edit
          </button>
          <button
            type='button'
            className='text-[#EF4444] hover:opacity-80'
            aria-label={`Remove ${item.firstName} ${item.lastName}`}
            onClick={() => onDeleteStakeholder(item.tenantStakeholderId)}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    ))}

    <div className='flex items-start gap-4 px-5 py-4'>
      <div className={`${iconWrapClass} mt-0.5`}>
        <UserPlus size={14} />
      </div>
      <div className='min-w-0 flex-1 space-y-3'>
        <div>
          <div className='text-[14px] font-semibold text-main'>
            {editingStakeholderId ? 'Edit stakeholder' : 'Add stakeholder'}
          </div>
          <div className='mt-0.5 text-[12px] text-[#6B7285]'>First name, last name, and email are required.</div>
        </div>
        <div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
          <input
            value={stakeholderForm.firstName}
            onChange={(e) => onStakeholderFormChange({ ...stakeholderForm, firstName: e.target.value })}
            className={fieldInputClass}
            placeholder='First name'
          />
          <input
            value={stakeholderForm.lastName}
            onChange={(e) => onStakeholderFormChange({ ...stakeholderForm, lastName: e.target.value })}
            className={fieldInputClass}
            placeholder='Last name'
          />
          <input
            type='email'
            value={stakeholderForm.email}
            onChange={(e) => onStakeholderFormChange({ ...stakeholderForm, email: e.target.value })}
            className={fieldInputClass}
            placeholder='Email'
          />
        </div>
        <div className='flex justify-end'>
          <button
            type='button'
            onClick={onSaveStakeholder}
            disabled={savingStakeholder}
            className={dashboardPrimaryCtaClass}
          >
            <Plus size={12} strokeWidth={2} />
            {savingStakeholder
              ? 'Saving...'
              : editingStakeholderId
                ? 'Update stakeholder'
                : 'Add stakeholder'}
          </button>
        </div>
      </div>
    </div>
  </SettingsGroup>
);

export default BusinessDetailsCard;
